import { IStringLocalizer } from "../../utilities/IStringLocalizer";
import { CertificationBase } from "../Models/Certifications";
import { CertificationType } from "../Models/Documents/DocumentModels";

/**
 * Helper class for rendering friendly names and other data for
 * certifications
 */
export default class CertificationRendererManager {
    private static _certificationTypeToFriendlyEnglishName: Readonly<{ [type: string]: string }> = {
        [CertificationType.Other]: "Other",
        [CertificationType.C2c]: "Cradle 2 Cradle",
        [CertificationType.Declare]: "Declare",
        [CertificationType.Epd]: "EPD",
        [CertificationType.Hpd]: "HPD",
        [CertificationType.Voc]: "VOC Certification",
        [CertificationType.UlGreenGuard]: "UL GreenGuard",
        [CertificationType.UlGreenGuardGold]: "UL GreenGuard Gold",
        [CertificationType.ScsIndoorAdvantage]: "SCS Indoor Advantage",
        [CertificationType.ScsIndoorAdvantageGold]: "SCS Indoor Advantage Gold",
        [CertificationType.BimfaLevel]: "BIFMA Level",
        [CertificationType.EnergyStar]: "Energy STAR",
        [CertificationType.SaferChoice]: "Safer Choice",
        [CertificationType.Lpc]: "LPC",
        [CertificationType.Nsf140]: "NSF 140",
        [CertificationType.Nsf332]: "NSF 332",
        [CertificationType.GreenCircleCertifiedEnvironmentalFacts]: "Green Circle Certified Environmental Facts",
        [CertificationType.GreenCircleCertifiedSustainabilityFacts]: "Green Circle Certified Sustainability Facts",
        [CertificationType.UlEcologo]: "UL ECOLOGO",
        [CertificationType.AnsiNsc373]: "ANSI NSC 373",
        [CertificationType.UlProductLens]: "UL Product Lens",
        [CertificationType.GreenCircleCertifiedManufacturerInventoryVerification]: "Green Circle Certified Manufacturer Inventory Verification",
        [CertificationType.C2cMaterialHealth]: "Cradle 2 Cradle Material Health",
        [CertificationType.Greenscreen]: "Greenscreen",
        [CertificationType.GreenHealthApproved]: "Green Health Approved",
        [CertificationType.GreenSeal]: "Green Seal",
        [CertificationType.CdphStandardMethodv122017]: "CDPH Standard Method v1.2-2017",
        [CertificationType.Floorscore]: "Floorscore",
        [CertificationType.CriGreenLabelPlus]: "CRI Green Label Plus",
        [CertificationType.MasCertifiedGreen]: "Mas Certified Green",
        [CertificationType.IntertekCleanAir]: "Intertek Clean Air",
        [CertificationType.TscaTitleViNaf]: "TSCA Title VI NAF",
        [CertificationType.TscaTitleViUlef]: "TSCA Title VI ULEF",
        [CertificationType.Lca]: "LCA",
        [CertificationType.GreenCircleCertifiedLifeCycleAssessmentOptimized]: "Green Circle Certified Life Cycle Assessment Optimized",
        [CertificationType.ScsCarbonNeutral]: "SCS Carbon Neutral",
        [CertificationType.GreenCircleCertifiedEnergySavings]: "Energy Savings",
        [CertificationType.FcsCertified]: "FSC Certified",
        [CertificationType.SfiCertified]: "SFI Certified",
        [CertificationType.UsdaCertifiedBiobased]: "USDA Certified Biobased",
        [CertificationType.GreenCircleCertifiedBiobasedContent]: "Green Circle Certified Biobased Content",
        [CertificationType.UlEnvironmentalClaimValidationBiobasedContent]: "UL Environmental ClaimValidation Biobased Content",
        [CertificationType.GreenCircleCertifiedRecycledContent]: "Green Circle Certified Recycled Content",
        [CertificationType.UlEnvironmentalClaimValidationRecycledContent]: "UL Environmental Claim Validation Recycled Content",
        [CertificationType.ScsRecycledContentCertified]: "SCS Recycled Content Certified",
        [CertificationType.GlobalRecycledStandard]: "Global Recycled Standard",
        [CertificationType.GreenCircleCertifiedClosedLoopProduct]: "Green Circle Certified Closed Loop Product",
        [CertificationType.GreenCircleCertifiedRecyclableMaterial]: "Green Circle Certified Recyclable Material",
        [CertificationType.UsEpdWaterSense]: "US EPA WaterSense"
    };

    // ===============================================================================

    public static getFriendlyNameForCertificationType(type: CertificationType, localizer: IStringLocalizer) {
        if (type === CertificationType.Other) {
            return localizer.localizeString("Other");
        }

        const name = CertificationRendererManager._certificationTypeToFriendlyEnglishName[type];

        if (!name) {
            console.error(`Unsupported Certification Type: ${type}`);
            return "?";
        }

        return localizer.localizeString(name);
    }

    // ===============================================================================

    public static getFriendlyExpirationDateStringForCertification(certification: CertificationBase, localizer: IStringLocalizer) {
        if (certification.expirationDate === null) {
            return localizer.localizeString("None");
        }

        return new Date(certification.expirationDate).toLocaleDateString();
    }
}